// src/services/customers.ts
import { apiJson, apiRequest } from './http';

export type SectorDTO = {
  id: number;
  name: string;
  icon?: string | null;
  active: boolean;
};

export type CustomerDTO = {
  id: number;
  businessName: string;
  vatNumber?: string;
  taxCode?: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  postalCode?: string;
  province?: string;
  country?: string;
  notes?: string;
  credentials?: string;
  isActive?: boolean;
  hasUserAccess?: boolean;
  userId?: number | null;
  userEmail?: string | null;
  services?: Array<{ id: number; name: string; enabled?: boolean }>;
  createdAt?: string;
  updatedAt?: string;
  // ▼ nuovo: settore lato FE (opzionale)
  sector?: SectorDTO | null;
};

export type CreateCustomerRequest = {
  businessName: string;
  vatNumber?: string;
  taxCode?: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  postalCode?: string;
  province?: string;
  country?: string;
  notes?: string;
  credentials?: string;
  // associazione settore (opzionale)
  sectorId?: number | null;
  createUserAccess?: boolean;
  userEmail?: string;
  userFirstName?: string;
  userLastName?: string;
  serviceIds?: number[];
};

export type Page<T> = {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
};

export type CustomerFileDTO = {
  id: number;
  customerId: number;
  uploadedById: number;
  filename: string;
  url?: string | null;
  contentType?: string | null;
  sizeBytes?: number | null;
  note?: string | null;
  uploadedAt: string;
};

// helper: normalizza risposta in array
function toArray<T>(res: any): T[] {
  if (Array.isArray(res)) return res as T[];
  if (res && Array.isArray(res.content)) return res.content as T[];
  return [];
}

// --- CRUD clienti --- //

export async function listCustomers(): Promise<CustomerDTO[]> {
  const res = await apiJson<any>('/p/customers');
  return toArray<CustomerDTO>(res);
}

export async function getCustomer(id: number): Promise<CustomerDTO> {
  return apiJson<CustomerDTO>(`/p/customers/${id}`);
}

export async function createCustomer(payload: CreateCustomerRequest): Promise<CustomerDTO> {
  return apiJson<CustomerDTO>('/p/customers', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function updateCustomer(
  id: number,
  payload: Partial<CreateCustomerRequest>
): Promise<CustomerDTO> {
  return apiJson<CustomerDTO>(`/p/customers/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export async function deleteCustomer(id: number): Promise<void> {
  await apiJson<void>(`/p/customers/${id}`, { method: 'DELETE' });
}

export async function listCustomersByService(serviceId: number): Promise<CustomerDTO[]> {
  const res = await apiJson<any>(`/p/customers/by-service/${serviceId}`);
  return toArray<CustomerDTO>(res);
}

export async function listServices(): Promise<Array<{ id: number; name: string }>> {
  return apiJson<Array<{ id: number; name: string }>>('/p/services');
}

// --- FILES per Customer --- //

export async function listCustomerFiles(
  customerId: number,
  page = 0,
  size = 10
): Promise<Page<CustomerFileDTO>> {
  return apiJson<Page<CustomerFileDTO>>(
    `/p/customers/${customerId}/files?page=${page}&size=${size}`
  );
}

export async function uploadCustomerFiles(
  customerId: number,
  files: File[],
  note?: string | null
): Promise<CustomerFileDTO[]> {
  const form = new FormData();
  files.forEach(f => form.append('files', f));
  if (note) form.append('note', note);

  const res = await apiRequest(`/p/customers/${customerId}/files`, {
    method: 'POST',
    body: form,
  });
  if (!res.ok) {
    const msg = await res.text().catch(() => '');
    throw new Error(msg || `Errore upload file (${res.status})`);
  }
  return res.json();
}

export async function deleteCustomerFile(customerId: number, fileId: number): Promise<void> {
  await apiJson<void>(`/p/customers/${customerId}/files/${fileId}`, { method: 'DELETE' });
}

export async function downloadCustomerFileBlob(customerId: number, fileId: number): Promise<Blob> {
  const res = await apiRequest(`/p/customers/${customerId}/files/${fileId}/download`, {
    method: 'GET',
  });
  if (!res.ok) {
    const txt = await res.text().catch(() => '');
    throw new Error(txt || `Errore download (${res.status})`);
  }
  return res.blob();
}
